import { useState, useRef, useEffect } from 'react';
import { useNotifications } from '@/contexts/NotificationContext';
import { Bell, Check, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

const typeIcons = {
  deposit_request: { color: 'text-amber-500', bg: 'bg-amber-500/10' },
  withdrawal_request: { color: 'text-amber-500', bg: 'bg-amber-500/10' },
  deposit_approved: { color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  withdrawal_approved: { color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  deposit_rejected: { color: 'text-red-500', bg: 'bg-red-500/10' },
  withdrawal_rejected: { color: 'text-red-500', bg: 'bg-red-500/10' },
  profit_credit: { color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  loss_debit: { color: 'text-red-500', bg: 'bg-red-500/10' },
  notice: { color: 'text-blue-500', bg: 'bg-blue-500/10' },
  announcement: { color: 'text-purple-500', bg: 'bg-purple-500/10' },
};

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllRead, soundEnabled, setSoundEnabled, playSound } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleSound = async () => {
    const newVal = !soundEnabled;
    setSoundEnabled(newVal);
    if (newVal) playSound('success');
    try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notification-settings`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email_enabled: true, sound_enabled: newVal, push_enabled: true })
    }); } catch {}
  };

  const getStyle = (type) => typeIcons[type] || { color: 'text-brand-text-secondary', bg: 'bg-brand-bg' };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-brand-text-secondary hover:text-brand-text-primary hover:bg-white/60 transition-all duration-200"
        data-testid="notification-bell"
      >
        <Bell size={20} strokeWidth={1.5} className={unreadCount > 0 ? 'animate-[wiggle_0.5s_ease-in-out]' : ''} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-brand-secondary text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white border border-brand-border rounded-lg shadow-xl z-50 overflow-hidden animate-fade-in" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border bg-brand-bg/50">
            <h3 className="text-sm font-semibold text-brand-text-primary">Notifications</h3>
            <div className="flex items-center gap-2">
              <button onClick={toggleSound} className="p-1 rounded hover:bg-brand-surface-hover" data-testid="toggle-sound-btn" title={soundEnabled ? 'Sound on' : 'Sound off'}>
                {soundEnabled ? <Volume2 size={14} className="text-brand-accent" /> : <VolumeX size={14} className="text-brand-text-secondary" />}
              </button>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-brand-primary hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                  <CheckCheck size={12} /> Read all
                </button>
              )}
            </div>
          </div>
          <ScrollArea className="max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-brand-text-secondary text-sm">No notifications yet</div>
            ) : (
              <div className="divide-y divide-brand-border/50">
                {notifications.map(n => {
                  const style = getStyle(n.type);
                  return (
                    <div
                      key={n.id}
                      onClick={() => !n.read && markAsRead(n.id)}
                      className={`px-4 py-3 cursor-pointer transition-colors hover:bg-brand-surface-hover ${!n.read ? 'bg-brand-primary/[0.02]' : ''}`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <div className={`w-2 h-2 rounded-full ${style.color.replace('text-', 'bg-')}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm leading-tight ${!n.read ? 'font-semibold text-brand-text-primary' : 'text-brand-text-secondary'}`}>{n.title}</p>
                            {!n.read && <div className="w-2 h-2 rounded-full bg-brand-secondary shrink-0 mt-1.5" />}
                          </div>
                          <p className="text-xs text-brand-text-secondary mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-brand-text-secondary/60 mt-1">{n.created_at ? timeAgo(n.created_at) : ''}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
